// src/components/resources/glossary/GlossaryFilters.tsx

'use client'

import { motion, AnimatePresence } from 'motion/react'
import { useState, useEffect, useDeferredValue, useMemo } from 'react'
import RiSearchLine from '~/icons/remix/generated/RiSearchLine'
import RiFilterLine from '~/icons/remix/generated/RiFilterLine'
import RiCloseLine from '~/icons/remix/generated/RiCloseLine'
import type { GlossaryCategory } from '~/lib/resources/types/shared/enums'
import type { GlossaryTerm } from '~/lib/resources/types/glossary/terms'

interface GlossaryFiltersProps {
  category: {
    id: GlossaryCategory
    label: string
    description: string
    icon: string
  }
  terms: GlossaryTerm[]
  onFilter: (filteredTerms: GlossaryTerm[]) => void
  isLoading?: boolean
}

export function GlossaryFilters({ category, terms, onFilter, isLoading }: GlossaryFiltersProps): JSX.Element {
  const [searchQuery, setSearchQuery] = useState('')
  const [showFilters, setShowFilters] = useState(false)
  const [selectedTags, setSelectedTags] = useState<string[]>([])

  // Defer the inputs to keep keystrokes/taps responsive on mobile - the actual
  // filter pass runs at a lower priority via `useDeferredValue`.
  const deferredQuery = useDeferredValue(searchQuery)
  const deferredTags = useDeferredValue(selectedTags)

  // Get unique tags from all terms
  const allTags = useMemo(
    () =>
      Array.from(new Set(terms.flatMap(term => term.tags ?? []))).sort((a, b) =>
        a.localeCompare(b)
      ),
    [terms]
  )

  const filteredTerms = useMemo(() => {
    const query = deferredQuery.trim().toLowerCase()
    const tags = deferredTags

    if (!query && tags.length === 0) {
      return terms
    }

    return terms.filter(term => {
      const matchesSearch = !query ||
        term.title.toLowerCase().includes(query) ||
        term.shortDescription.toLowerCase().includes(query) ||
        term.definition.toLowerCase().includes(query) ||
        term.tags?.some(tag => tag.toLowerCase().includes(query))

      const matchesTags = tags.length === 0 ||
        tags.every(tag => term.tags?.includes(tag))

      return matchesSearch && matchesTags
    })
  }, [terms, deferredQuery, deferredTags])

  useEffect(() => {
    onFilter(filteredTerms)
  }, [filteredTerms, onFilter])

  // Handle tag selection
  const handleTagToggle = (tag: string): void => {
    const newTags = selectedTags.includes(tag)
      ? selectedTags.filter(t => t !== tag)
      : [...selectedTags, tag]

    setSelectedTags(newTags)
  }

  return (
    <div className="space-y-4">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between"
      >
        {/* Search */}
        <div className="relative flex-1 max-w-lg">
          <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-4">
            <RiSearchLine className="h-5 w-5 text-gray-400" />
          </div>
          <input
            id="glossary-search"
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            disabled={isLoading}
            className="block w-full rounded-xl border-2 border-gray-200 bg-white/50 py-3 pl-11 pr-4 
                     text-gray-900 placeholder:text-gray-500 focus:bg-white focus:outline-none 
                     focus:ring-2 focus:ring-indigo-500 focus:border-transparent
                     dark:border-gray-800 dark:bg-gray-900/50 dark:text-white 
                     dark:placeholder:text-gray-400 dark:focus:bg-gray-900
                     disabled:opacity-50 disabled:cursor-not-allowed"
            placeholder={`Search ${category.label.toLowerCase()}...`}
          />
        </div>

        {/* Filters */}
        <div className="flex items-center gap-4">
          <button
            type="button"
            onClick={() => setShowFilters(!showFilters)}
            disabled={isLoading}
            className="inline-flex items-center gap-2 rounded-lg bg-white px-4 py-2.5 text-sm font-medium text-gray-700 shadow-sm ring-1 ring-gray-200 hover:bg-gray-50 dark:bg-gray-800 dark:text-gray-300 dark:ring-gray-700 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {showFilters ? (
              <RiCloseLine className="h-5 w-5" />
            ) : (
              <RiFilterLine className="h-5 w-5" />
            )}
            {showFilters ? 'Close' : 'Filter'}
            {selectedTags.length > 0 && (
              <span className="ml-1 rounded-full bg-indigo-100 px-2 py-0.5 text-xs font-medium text-indigo-700 dark:bg-indigo-900/40 dark:text-indigo-400">
                {selectedTags.length}
              </span>
            )}
          </button>
        </div>
      </motion.div>

      {/* Filter Tags */}
      <AnimatePresence>
        {showFilters && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="flex flex-wrap gap-2 pt-4"
          >
            {allTags.map(tag => (
              <button
                key={tag}
                onClick={() => handleTagToggle(tag)}
                disabled={isLoading}
                className={`inline-flex items-center rounded-full px-3 py-1 text-sm font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
                  selectedTags.includes(tag)
                    ? 'bg-indigo-100 text-indigo-700 dark:bg-indigo-900/40 dark:text-indigo-400'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-700'
                }`}
              >
                {tag}
              </button>
            ))}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
} 